import { ACCOUNTING_SYSTEM_API_BASE_URL, LANGUAGE_KEY } from '../../resources/constants';
import { store } from '../../store';
import { toMoneyValue } from './money';
import type {
  AllocationChange,
  AllocationHistoryEntry,
  AllocationInput,
  BranchId,
  Customer,
  CustomerAllocation,
  CustomerDeposit,
  CustomerDetail,
  CustomerFinancial,
  CustomerId,
  CustomerImportResult,
  CustomerListOrder,
  CustomerListSort,
  CustomerLookups,
  CustomerPreferences,
  CustomerRow,
  CustomerStatusFilter,
  DepositDetail,
  DepositId,
  OpenDeposit,
  OpenDepositStatus,
  Paginated,
} from './types';

/**
 * REV-1398 Customer Center API (stable GET contract, see
 * /tmp/rev1398-delivery-contract.txt).
 *
 * Base /api/v1/customer-center, no trailing slash; outer ResponseModel
 * {data,status_code,success,message}; every request carries branch_id.
 * Customer amount is the explicit allocation row amount, never ledger sums.
 * All money is returned as fixed two-decimal strings (or null when absent).
 * Nullable metadata (parent_id, date, description, customer_name) keeps rows.
 * No placeholder or mock responses.
 */

/** Plain object check for server error envelope fields. */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Server `error` object (machine code plus params) when it is a plain object; otherwise null. */
function errorEnvelope(body: unknown): Record<string, unknown> | null {
  if (!isPlainRecord(body)) return null;
  return isPlainRecord(body.error) ? body.error : null;
}

/** Machine code from `error.code`; null when absent. */
function errorMessageKey(body: unknown): string | null {
  const code: unknown = errorEnvelope(body)?.code;
  return typeof code === 'string' && code.trim() !== '' ? code : null;
}

/** Translated display text from top-level `message`; null when absent. */
function errorMessageText(body: unknown): string | null {
  if (!isPlainRecord(body)) return null;
  const message: unknown = body.message;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}

/** Error preserving HTTP status so callers can distinguish 401/403, plus server message fields. */
export class ApiError extends Error {
  status: number;
  data: unknown;
  /** Machine code from server `error.code` (e.g. customer_center.allocation_exceeds_deposit); null when absent. */
  messageKey: string | null;
  /** Translated display text from server `message`; null when absent. */
  messageText: string | null;
  /** Server `error` object with code and params (e.g. deposit_id, allocated); null when absent. */
  params: Record<string, unknown> | null;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.messageKey = errorMessageKey(data);
    this.messageText = errorMessageText(data);
    this.params = errorEnvelope(data);
  }
}

/** Fails visibly when the accounting API base is absent instead of building an "undefined" URL. */
function customerCenterBase(): string {
  if (!ACCOUNTING_SYSTEM_API_BASE_URL) {
    throw new ApiError(
      'Accounting API base URL is not configured (REACT_APP_ACCOUNTING_SYSTEM_API_BASE_URL).',
      0,
      null,
    );
  }
  return `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/customer-center`;
}

function isIdentifier(value: unknown): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

function failureMessage(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null) {
    if ('detail' in body && typeof body.detail === 'string' && body.detail.trim()) return body.detail;
    if ('message' in body && typeof body.message === 'string' && body.message.trim()) return body.message;
    if ('error' in body && typeof body.error === 'string' && body.error.trim()) return body.error;
  }
  return fallback;
}

async function request<T>(url: string, init: RequestInit, parse: (body: unknown) => T): Promise<T> {
  const token: unknown = store.getState().user?.accessToken;
  const language = localStorage.getItem(LANGUAGE_KEY) || 'en';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': language,
    'ngrok-skip-browser-warning': 'true',
  };
  if (typeof token === 'string' && token.length > 0) {
    headers.Authorization = `Bearer ${token}`;
  }
  const resp = await fetch(url, { ...init, headers: { ...headers, ...(init.headers || {}) } });
  let body: unknown = {};
  try {
    body = await resp.json();
  } catch {
    body = {};
  }
  if (!resp.ok) {
    throw new ApiError(failureMessage(body, `Request failed (${resp.status})`), resp.status, body);
  }
  if (typeof body === 'object' && body !== null) {
    const failed =
      ('success' in body && body.success === false) ||
      ('status_code' in body && typeof body.status_code === 'number' && body.status_code >= 400);
    if (failed) {
      const code: unknown = 'status_code' in body ? body.status_code : resp.status;
      throw new ApiError(
        failureMessage(body, `Request failed (${resp.status})`),
        typeof code === 'number' ? code : resp.status,
        body,
      );
    }
  }
  return parse(body);
}

/** Exact wrapper: outer ResponseModel {data,...}; nothing else is unwrapped. */
function unwrapData(body: unknown): unknown {
  if (typeof body === 'object' && body !== null && 'data' in body) {
    return body.data;
  }
  return body;
}

/** Customer list for a branch. includeInactive=false limits results to active customers (picker). */
export async function fetchCustomers(
  branchId: BranchId,
  search: string,
  page: number,
  perPage: number,
  signal?: AbortSignal,
  includeInactive = true,
): Promise<Paginated<Customer>> {
  const params = new URLSearchParams({
    branch_id: String(branchId),
    page: String(page),
    per_page: String(perPage),
    include_inactive: String(includeInactive),
  });
  const q = search.trim();
  if (q) params.set('search', q);
  return request(`${customerCenterBase()}/customers?${params.toString()}`, { method: 'GET', signal }, (body) => {
    const node: unknown = unwrapData(body);
    let items: Customer[] = [];
    let total = 0;
    let outPage = page;
    let outPerPage = perPage;
    if (typeof node === 'object' && node !== null) {
      if ('items' in node && Array.isArray(node.items)) {
        const parsed: Customer[] = [];
        for (const entry of node.items) {
          if (typeof entry === 'object' && entry !== null && 'id' in entry && 'name' in entry) {
            const id: unknown = entry.id;
            const name: unknown = entry.name;
            if (isIdentifier(id) && typeof name === 'string') {
              const active: unknown = 'active' in entry ? entry.active : true;
              parsed.push({ id, name, active: typeof active === 'boolean' ? active : true });
            }
          }
        }
        items = parsed;
      }
      if ('total' in node && typeof node.total === 'number' && Number.isFinite(node.total)) {
        total = node.total;
      } else if (items.length > 0) {
        total = items.length;
      }
      if ('page' in node && typeof node.page === 'number' && node.page > 0) outPage = node.page;
      if ('per_page' in node && typeof node.per_page === 'number' && node.per_page > 0) {
        outPerPage = node.per_page;
      }
    }
    return { items, total, page: outPage, per_page: outPerPage };
  });
}

/** Existing name for the customer list; same behavior, includeInactive defaults to true. */
export const listCustomers = fetchCustomers;

export async function listCustomerDeposits(
  branchId: BranchId,
  customerId: CustomerId,
  page: number,
  perPage: number,
  signal?: AbortSignal,
): Promise<Paginated<CustomerDeposit>> {
  const params = new URLSearchParams({
    branch_id: String(branchId),
    page: String(page),
    per_page: String(perPage),
  });
  return request(
    `${customerCenterBase()}/customers/${encodeURIComponent(String(customerId))}/deposits?${params.toString()}`,
    { method: 'GET', signal },
    (body) => {
      const node: unknown = unwrapData(body);
      let items: CustomerDeposit[] = [];
      let total = 0;
      let outPage = page;
      let outPerPage = perPage;
      if (typeof node === 'object' && node !== null && 'items' in node && Array.isArray(node.items)) {
        const parsed: CustomerDeposit[] = [];
        for (const entry of node.items) {
          if (typeof entry !== 'object' || entry === null || !('id' in entry)) continue;
          const id: unknown = entry.id;
          if (!isIdentifier(id)) continue;
          const parentId: unknown = 'parent_id' in entry ? entry.parent_id : null;
          const date: unknown = 'date' in entry ? entry.date : null;
          const description: unknown = 'description' in entry ? entry.description : null;
          parsed.push({
            id,
            parent_id: isIdentifier(parentId) ? parentId : null,
            date: typeof date === 'string' && date.trim() !== '' ? date : null,
            description: typeof description === 'string' ? description : null,
            customer_amount: toMoneyValue('customer_amount' in entry ? entry.customer_amount : null),
            deposit_amount: toMoneyValue('deposit_amount' in entry ? entry.deposit_amount : null),
          });
        }
        items = parsed;
        if ('total' in node && typeof node.total === 'number' && Number.isFinite(node.total)) {
          total = node.total;
        } else {
          total = items.length;
        }
        if ('page' in node && typeof node.page === 'number' && node.page > 0) outPage = node.page;
        if ('per_page' in node && typeof node.per_page === 'number' && node.per_page > 0) {
          outPerPage = node.per_page;
        }
      }
      return { items, total, page: outPage, per_page: outPerPage };
    },
  );
}

export async function getDepositDetail(
  branchId: BranchId,
  depositId: DepositId,
  signal?: AbortSignal,
): Promise<DepositDetail> {
  const params = new URLSearchParams({ branch_id: String(branchId) });
  return request(
    `${customerCenterBase()}/deposits/${encodeURIComponent(String(depositId))}?${params.toString()}`,
    { method: 'GET', signal },
    parseDepositDetail,
  );
}

/** Open deposits for the branch (unallocated, partly assigned, or all open). Strict parse like listCustomerDeposits. */
export async function fetchOpenDeposits(
  branchId: BranchId,
  status: OpenDepositStatus,
  page: number,
  perPage: number,
  signal?: AbortSignal,
): Promise<Paginated<OpenDeposit>> {
  const params = new URLSearchParams({
    branch_id: String(branchId),
    status,
    page: String(page),
    per_page: String(perPage),
  });
  return request(`${customerCenterBase()}/deposits?${params.toString()}`, { method: 'GET', signal }, (body) => {
    const node: unknown = unwrapData(body);
    let items: OpenDeposit[] = [];
    let total = 0;
    let outPage = page;
    let outPerPage = perPage;
    if (typeof node === 'object' && node !== null && 'items' in node && Array.isArray(node.items)) {
      const parsed: OpenDeposit[] = [];
      for (const entry of node.items) {
        if (typeof entry !== 'object' || entry === null || !('id' in entry)) continue;
        const id: unknown = entry.id;
        if (!isIdentifier(id)) continue;
        const parentId: unknown = 'parent_id' in entry ? entry.parent_id : null;
        const date: unknown = 'date' in entry ? entry.date : null;
        const description: unknown = 'description' in entry ? entry.description : null;
        parsed.push({
          id,
          parent_id: isIdentifier(parentId) ? parentId : null,
          date: typeof date === 'string' && date.trim() !== '' ? date : null,
          description: typeof description === 'string' ? description : null,
          deposit_amount: toMoneyValue('deposit_amount' in entry ? entry.deposit_amount : null),
          allocated_amount: toMoneyValue('allocated_amount' in entry ? entry.allocated_amount : null),
          remaining_amount: toMoneyValue('remaining_amount' in entry ? entry.remaining_amount : null),
          suggested_customer: parseSuggestedCustomer(
            'suggested_customer' in entry ? entry.suggested_customer : null,
          ),
        });
      }
      items = parsed;
      if ('total' in node && typeof node.total === 'number' && Number.isFinite(node.total)) {
        total = node.total;
      } else {
        total = items.length;
      }
      if ('page' in node && typeof node.page === 'number' && node.page > 0) outPage = node.page;
      if ('per_page' in node && typeof node.per_page === 'number' && node.per_page > 0) {
        outPerPage = node.per_page;
      }
    }
    return { items, total, page: outPage, per_page: outPerPage };
  });
}

/**
 * Replace all explicit splits for one deposit (`[]` clears). Body is `{assignments}`, matching
 * CustomerAllocationBody in gl-api. Resolves to the refreshed deposit detail.
 */
export async function saveDepositAllocations(
  branchId: BranchId,
  depositId: DepositId,
  allocations: AllocationInput[],
): Promise<DepositDetail> {
  const params = new URLSearchParams({ branch_id: String(branchId) });
  const assignments = allocations.map((row) => ({ customer_id: row.customer_id, amount: row.amount }));
  return request(
    `${customerCenterBase()}/deposits/${encodeURIComponent(String(depositId))}/customers?${params.toString()}`,
    { method: 'PUT', body: JSON.stringify({ assignments }) },
    parseDepositDetail,
  );
}

/** Lenient suggested customer; null when absent or invalid. */
function parseSuggestedCustomer(value: unknown): Pick<Customer, 'id' | 'name'> | null {
  if (typeof value !== 'object' || value === null || !('id' in value) || !('name' in value)) return null;
  const id: unknown = value.id;
  const name: unknown = value.name;
  if (!isIdentifier(id) || typeof name !== 'string' || name.trim() === '') return null;
  return { id, name };
}

/** Strict allocation row; null when the customer id is missing or invalid. */
function parseAllocationRow(entry: unknown): CustomerAllocation | null {
  if (typeof entry !== 'object' || entry === null || !('customer_id' in entry)) return null;
  const cid: unknown = entry.customer_id;
  if (!isIdentifier(cid)) return null;
  const cname: unknown = 'customer_name' in entry ? entry.customer_name : null;
  const active: unknown = 'active' in entry ? entry.active : null;
  return {
    customer_id: cid,
    customer_name: typeof cname === 'string' && cname.trim() !== '' ? cname : null,
    amount: toMoneyValue('amount' in entry ? entry.amount : null),
    active: typeof active === 'boolean' ? active : null,
  };
}

/** Lenient: non-array yields []; invalid rows are dropped. */
function parseAllocationRows(value: unknown): CustomerAllocation[] {
  if (!Array.isArray(value)) return [];
  const rows: CustomerAllocation[] = [];
  for (const entry of value) {
    const row = parseAllocationRow(entry);
    if (row !== null) rows.push(row);
  }
  return rows;
}

/** Lenient: null or non-object yields null. */
function parseAllocationChange(value: unknown): AllocationChange | null {
  if (typeof value !== 'object' || value === null) return null;
  const byUser: unknown = 'by_user_id' in value ? value.by_user_id : null;
  const byName: unknown = 'by_name' in value ? value.by_name : null;
  const at: unknown = 'at' in value ? value.at : null;
  const source: unknown = 'source' in value ? value.source : null;
  return {
    by_user_id: typeof byUser === 'number' && Number.isFinite(byUser) ? byUser : null,
    by_name: typeof byName === 'string' && byName.trim() !== '' ? byName : null,
    at: typeof at === 'string' && at.trim() !== '' ? at : null,
    source: typeof source === 'string' && source.trim() !== '' ? source : 'unknown',
  };
}

/** Lenient: non-array yields []; entries that are not objects are skipped. */
function parseAllocationHistory(value: unknown): AllocationHistoryEntry[] {
  if (!Array.isArray(value)) return [];
  const entries: AllocationHistoryEntry[] = [];
  for (const item of value) {
    if (typeof item !== 'object' || item === null) continue;
    const change = parseAllocationChange(item);
    if (change === null) continue;
    entries.push({
      ...change,
      before: parseAllocationRows('before' in item ? item.before : null),
      after: parseAllocationRows('after' in item ? item.after : null),
    });
  }
  return entries;
}

/** Strict deposit detail parser shared by GET detail and PUT allocations (same response shape). */
function parseDepositDetail(body: unknown): DepositDetail {
  function malformed(): never {
    throw new ApiError('Malformed deposit detail response.', 200, body);
  }
  const node: unknown = unwrapData(body);
  if (typeof node !== 'object' || node === null || !('id' in node)) malformed();
  const id: unknown = node.id;
  if (!isIdentifier(id)) malformed();
  const parentRaw: unknown = 'parent_id' in node ? node.parent_id : null;
  if (parentRaw !== null && !isIdentifier(parentRaw)) malformed();
  const parentId: string | number | null = isIdentifier(parentRaw) ? parentRaw : null;
  const date: unknown = 'date' in node ? node.date : null;
  const description: unknown = 'description' in node ? node.description : null;
  const breakdownsRaw: unknown = 'breakdowns' in node ? node.breakdowns : null;
  if (!Array.isArray(breakdownsRaw)) malformed();
  const breakdownsList: unknown[] = breakdownsRaw;
  const allocationsRaw: unknown = 'allocations' in node ? node.allocations : null;
  if (!Array.isArray(allocationsRaw)) malformed();
  const allocationsList: unknown[] = allocationsRaw;

  const detail: DepositDetail = {
    id,
    parent_id: parentId,
    date: typeof date === 'string' && date.trim() !== '' ? date : null,
    description: typeof description === 'string' ? description : null,
    deposit_amount: toMoneyValue('deposit_amount' in node ? node.deposit_amount : null),
    breakdowns: [],
    allocations: [],
    last_changed: parseAllocationChange('last_changed' in node ? node.last_changed : null),
    history: parseAllocationHistory('history' in node ? node.history : null),
    suggested_customer: parseSuggestedCustomer(
      'suggested_customer' in node ? node.suggested_customer : null,
    ),
  };
  for (const entry of breakdownsList) {
    if (typeof entry !== 'object' || entry === null || !('id' in entry)) malformed();
    const bid: unknown = entry.id;
    if (!isIdentifier(bid)) malformed();
    const row: DepositDetail['breakdowns'][number] = {
      id: bid,
      amount: toMoneyValue('amount' in entry ? entry.amount : null),
    };
    if ('account_type' in entry && typeof entry.account_type === 'string') {
      row.account_type = entry.account_type;
    }
    if ('txn_type' in entry && typeof entry.txn_type === 'string') row.txn_type = entry.txn_type;
    if ('description' in entry && typeof entry.description === 'string') {
      row.description = entry.description;
    }
    if ('master_chart_of_account_id' in entry && isIdentifier(entry.master_chart_of_account_id)) {
      row.master_chart_of_account_id = entry.master_chart_of_account_id;
    }
    detail.breakdowns.push(row);
  }
  for (const entry of allocationsList) {
    const row = parseAllocationRow(entry);
    if (row === null) malformed();
    detail.allocations.push(row);
  }
  return detail;
}

function parseOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function parseCustomerRow(entry: unknown): CustomerRow | null {
  if (typeof entry !== 'object' || entry === null || !('id' in entry) || !('name' in entry)) return null;
  const id: unknown = entry.id;
  const name: unknown = entry.name;
  if (!isIdentifier(id) || typeof name !== 'string') return null;
  const active: unknown = 'active' in entry ? entry.active : true;
  const customerId: unknown = 'customer_id' in entry ? entry.customer_id : '';
  return {
    id,
    name,
    active: typeof active === 'boolean' ? active : true,
    customer_id: typeof customerId === 'string' ? customerId : String(customerId ?? ''),
    display_name: parseOptionalString('display_name' in entry ? entry.display_name : null),
    email: parseOptionalString('email' in entry ? entry.email : null),
    phone: parseOptionalString('phone' in entry ? entry.phone : null),
    category_id:
      'category_id' in entry && typeof entry.category_id === 'number' ? entry.category_id : null,
    category_name: parseOptionalString('category_name' in entry ? entry.category_name : null),
    linked_deposits_count:
      'linked_deposits_count' in entry && typeof entry.linked_deposits_count === 'number'
        ? entry.linked_deposits_count
        : 0,
  };
}

function parseFinancial(node: unknown): CustomerFinancial {
  const base: CustomerFinancial = {
    currency: 'USD',
    hold_status: false,
    credit_limit: null,
    receivable_account: '',
    tax_id: '',
    taxable: false,
    tax_code_id: null,
    payment_term_id: null,
  };
  if (typeof node !== 'object' || node === null) return base;
  if ('currency' in node && typeof node.currency === 'string') base.currency = node.currency;
  if ('hold_status' in node && typeof node.hold_status === 'boolean') base.hold_status = node.hold_status;
  if ('credit_limit' in node) {
    const raw = node.credit_limit;
    base.credit_limit = raw === null ? null : String(raw);
  }
  if ('receivable_account' in node && typeof node.receivable_account === 'string') {
    base.receivable_account = node.receivable_account;
  }
  if ('tax_id' in node && typeof node.tax_id === 'string') base.tax_id = node.tax_id;
  if ('taxable' in node && typeof node.taxable === 'boolean') base.taxable = node.taxable;
  if ('tax_code_id' in node && typeof node.tax_code_id === 'number') base.tax_code_id = node.tax_code_id;
  if ('payment_term_id' in node && typeof node.payment_term_id === 'number') {
    base.payment_term_id = node.payment_term_id;
  }
  return base;
}

function parsePreferences(node: unknown): CustomerPreferences {
  const base: CustomerPreferences = { preferred_communication: false, preferred_transactions: [] };
  if (typeof node !== 'object' || node === null) return base;
  if ('preferred_communication' in node && typeof node.preferred_communication === 'boolean') {
    base.preferred_communication = node.preferred_communication;
  }
  if ('preferred_transactions' in node && Array.isArray(node.preferred_transactions)) {
    base.preferred_transactions = node.preferred_transactions.filter((v): v is string => typeof v === 'string');
  }
  return base;
}

function parseCustomerDetailNode(node: unknown): CustomerDetail {
  const row = parseCustomerRow(node);
  if (!row) throw new ApiError('Malformed customer detail response.', 200, node);
  const body: Record<string, unknown> = isPlainRecord(node) ? node : {};
  const fax = typeof body.fax === 'string' ? body.fax : '';
  const website = typeof body.website === 'string' ? body.website : '';
  const is1099 = typeof body.is_1099_enabled === 'boolean' ? body.is_1099_enabled : false;
  const ein = typeof body.ein === 'string' ? body.ein : '';
  const financial = parseFinancial('financial' in body ? body.financial : null);
  const preferences = parsePreferences('preferences' in body ? body.preferences : null);
  const contacts: CustomerDetail['contacts'] = [];
  const addresses: CustomerDetail['addresses'] = [];
  const contactList = body.contacts;
  if (Array.isArray(contactList)) {
    for (const item of contactList) {
      if (typeof item !== 'object' || item === null) continue;
      contacts.push({
        id: 'id' in item && typeof item.id === 'number' ? item.id : undefined,
        salutation: typeof item.salutation === 'string' ? item.salutation : '',
        first_name: typeof item.first_name === 'string' ? item.first_name : '',
        middle_name: typeof item.middle_name === 'string' ? item.middle_name : '',
        last_name: typeof item.last_name === 'string' ? item.last_name : '',
        title: typeof item.title === 'string' ? item.title : '',
        email: typeof item.email === 'string' ? item.email : '',
        phone: typeof item.phone === 'string' ? item.phone : '',
      });
    }
  }
  const addressList = body.addresses;
  if (Array.isArray(addressList)) {
    for (const item of addressList) {
      if (typeof item !== 'object' || item === null) continue;
      addresses.push({
        id: 'id' in item && typeof item.id === 'number' ? item.id : undefined,
        address_line1: typeof item.address_line1 === 'string' ? item.address_line1 : '',
        address_line2: typeof item.address_line2 === 'string' ? item.address_line2 : '',
        city: typeof item.city === 'string' ? item.city : '',
        state: typeof item.state === 'string' ? item.state : '',
        zip_code: typeof item.zip_code === 'string' ? item.zip_code : '',
        country: typeof item.country === 'string' ? item.country : '',
        default_billing: Boolean(item.default_billing),
        default_shipping: Boolean(item.default_shipping),
      });
    }
  }
  return { ...row, fax, website, is_1099_enabled: is1099, ein, financial, preferences, contacts, addresses };
}

export interface CustomerListQuery {
  search: string;
  status: CustomerStatusFilter;
  sort: CustomerListSort;
  order: CustomerListOrder;
  page: number;
  pageSize: number;
}

export async function fetchCustomerListPage(
  branchId: BranchId,
  query: CustomerListQuery,
  signal?: AbortSignal,
): Promise<{ items: CustomerRow[]; total: number; page: number; page_size: number }> {
  const params = new URLSearchParams({
    branch_id: String(branchId),
    page: String(query.page),
    page_size: String(query.pageSize),
    status: query.status,
    sort: query.sort,
    order: query.order,
  });
  const q = query.search.trim();
  if (q) params.set('search', q);
  return request(`${customerCenterBase()}/customers?${params.toString()}`, { method: 'GET', signal }, (body) => {
    const node: unknown = unwrapData(body);
    let items: CustomerRow[] = [];
    let total = 0;
    let outPage = query.page;
    let outPageSize = query.pageSize;
    if (typeof node === 'object' && node !== null) {
      if ('items' in node && Array.isArray(node.items)) {
        for (const entry of node.items) {
          const row = parseCustomerRow(entry);
          if (row) items.push(row);
        }
      }
      if ('total' in node && typeof node.total === 'number') total = node.total;
      if ('page' in node && typeof node.page === 'number') outPage = node.page;
      if ('page_size' in node && typeof node.page_size === 'number') outPageSize = node.page_size;
      else if ('per_page' in node && typeof node.per_page === 'number') outPageSize = node.per_page;
    }
    return { items, total, page: outPage, page_size: outPageSize };
  });
}

export async function fetchCustomerLookups(branchId: BranchId, signal?: AbortSignal): Promise<CustomerLookups> {
  const params = new URLSearchParams({ branch_id: String(branchId) });
  return request(`${customerCenterBase()}/customers/lookups?${params.toString()}`, { method: 'GET', signal }, (body) => {
    const node: unknown = unwrapData(body);
    const empty: CustomerLookups = { payment_terms: [], categories: [], tax_codes: [], states: [] };
    if (typeof node !== 'object' || node === null) return empty;
    const readIdName = (raw: unknown) => {
      if (!Array.isArray(raw)) return [];
      const out: Array<{ id: number; name: string }> = [];
      for (const entry of raw) {
        if (typeof entry !== 'object' || entry === null) continue;
        if ('id' in entry && typeof entry.id === 'number' && 'name' in entry && typeof entry.name === 'string') {
          out.push({ id: entry.id, name: entry.name });
        }
      }
      return out;
    };
    const readStates = (raw: unknown) => {
      if (!Array.isArray(raw)) return [];
      const out: Array<{ code: string; name: string }> = [];
      for (const entry of raw) {
        if (typeof entry !== 'object' || entry === null) continue;
        if ('code' in entry && typeof entry.code === 'string') {
          out.push({ code: entry.code, name: typeof entry.name === 'string' ? entry.name : entry.code });
        }
      }
      return out;
    };
    return {
      payment_terms: readIdName('payment_terms' in node ? node.payment_terms : null),
      categories: readIdName('categories' in node ? node.categories : null),
      tax_codes: readIdName('tax_codes' in node ? node.tax_codes : null),
      states: readStates('states' in node ? node.states : null),
    };
  });
}

export async function fetchCustomerDetail(
  branchId: BranchId,
  customerId: CustomerId,
  signal?: AbortSignal,
): Promise<CustomerDetail> {
  const params = new URLSearchParams({ branch_id: String(branchId) });
  return request(
    `${customerCenterBase()}/customers/${encodeURIComponent(String(customerId))}?${params.toString()}`,
    { method: 'GET', signal },
    (body) => parseCustomerDetailNode(unwrapData(body)),
  );
}

function customerWritePayload(detail: CustomerDetail): Record<string, unknown> {
  return {
    name: detail.name.trim(),
    display_name: detail.display_name?.trim() || null,
    email: detail.email?.trim() || null,
    phone: detail.phone?.trim() || null,
    fax: detail.fax.trim() || null,
    website: detail.website.trim() || null,
    category_id: detail.category_id,
    active: detail.active,
    is_1099_enabled: detail.is_1099_enabled,
    ein: detail.ein.trim() || null,
    financial: detail.financial,
    preferences: detail.preferences,
    contacts: detail.contacts.map((c) => ({
      ...(c.id ? { id: c.id } : {}),
      salutation: c.salutation,
      first_name: c.first_name,
      middle_name: c.middle_name,
      last_name: c.last_name,
      title: c.title,
      email: c.email,
      phone: c.phone,
    })),
    addresses: detail.addresses.map((a) => ({
      ...(a.id ? { id: a.id } : {}),
      address_line1: a.address_line1,
      address_line2: a.address_line2,
      city: a.city,
      state: a.state,
      zip_code: a.zip_code,
      country: a.country,
      default_billing: a.default_billing,
      default_shipping: a.default_shipping,
    })),
  };
}

export async function createCustomer(branchId: BranchId, detail: CustomerDetail): Promise<CustomerDetail> {
  const params = new URLSearchParams({ branch_id: String(branchId) });
  return request(
    `${customerCenterBase()}/customers?${params.toString()}`,
    { method: 'POST', body: JSON.stringify(customerWritePayload(detail)) },
    (body) => parseCustomerDetailNode(unwrapData(body)),
  );
}

export async function updateCustomer(
  branchId: BranchId,
  customerId: CustomerId,
  detail: CustomerDetail,
): Promise<CustomerDetail> {
  const params = new URLSearchParams({ branch_id: String(branchId) });
  return request(
    `${customerCenterBase()}/customers/${encodeURIComponent(String(customerId))}?${params.toString()}`,
    { method: 'PUT', body: JSON.stringify(customerWritePayload(detail)) },
    (body) => parseCustomerDetailNode(unwrapData(body)),
  );
}

export async function patchCustomerStatus(
  branchId: BranchId,
  customerId: CustomerId,
  active: boolean,
): Promise<CustomerRow> {
  const params = new URLSearchParams({ branch_id: String(branchId) });
  return request(
    `${customerCenterBase()}/customers/${encodeURIComponent(String(customerId))}/status?${params.toString()}`,
    { method: 'PATCH', body: JSON.stringify({ active }) },
    (body) => {
      const row = parseCustomerRow(unwrapData(body));
      if (!row) throw new ApiError('Malformed customer status response.', 200, body);
      return row;
    },
  );
}

async function authHeaders(contentType?: string): Promise<Record<string, string>> {
  const token: unknown = store.getState().user?.accessToken;
  const language = localStorage.getItem(LANGUAGE_KEY) || 'en';
  const headers: Record<string, string> = {
    'Accept-Language': language,
    'ngrok-skip-browser-warning': 'true',
  };
  if (contentType) headers['Content-Type'] = contentType;
  if (typeof token === 'string' && token.length > 0) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export async function exportCustomersFile(
  branchId: BranchId,
  format: 'csv' | 'xlsx',
  search: string,
  status: CustomerStatusFilter,
): Promise<Blob> {
  const params = new URLSearchParams({ branch_id: String(branchId), format, status });
  if (search.trim()) params.set('search', search.trim());
  const resp = await fetch(`${customerCenterBase()}/customers/export?${params.toString()}`, {
    method: 'GET',
    headers: await authHeaders(),
  });
  if (!resp.ok) {
    let body: unknown = {};
    try {
      body = await resp.json();
    } catch {
      body = {};
    }
    throw new ApiError(failureMessage(body, `Export failed (${resp.status})`), resp.status, body);
  }
  return resp.blob();
}

export async function downloadCustomerImportTemplate(branchId: BranchId): Promise<Blob> {
  const params = new URLSearchParams({ branch_id: String(branchId) });
  const resp = await fetch(`${customerCenterBase()}/customers/import/template?${params.toString()}`, {
    method: 'GET',
    headers: await authHeaders(),
  });
  if (!resp.ok) {
    let body: unknown = {};
    try {
      body = await resp.json();
    } catch {
      body = {};
    }
    throw new ApiError(failureMessage(body, `Template failed (${resp.status})`), resp.status, body);
  }
  return resp.blob();
}

export async function importCustomersFile(branchId: BranchId, file: File): Promise<CustomerImportResult> {
  const params = new URLSearchParams({ branch_id: String(branchId) });
  const form = new FormData();
  form.append('file', file);
  const resp = await fetch(`${customerCenterBase()}/customers/import?${params.toString()}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: form,
  });
  let body: unknown = {};
  try {
    body = await resp.json();
  } catch {
    body = {};
  }
  if (!resp.ok) {
    throw new ApiError(failureMessage(body, `Import failed (${resp.status})`), resp.status, body);
  }
  const node: unknown = unwrapData(body);
  const empty: CustomerImportResult = { imported: 0, failed: 0, errors: [] };
  if (typeof node !== 'object' || node === null) return empty;
  const imported = 'imported' in node && typeof node.imported === 'number' ? node.imported : 0;
  const failed = 'failed' in node && typeof node.failed === 'number' ? node.failed : 0;
  const errors: CustomerImportResult['errors'] = [];
  if ('errors' in node && Array.isArray(node.errors)) {
    for (const entry of node.errors) {
      if (typeof entry !== 'object' || entry === null) continue;
      errors.push({
        row: typeof entry.row === 'number' ? entry.row : 0,
        customer_name: typeof entry.customer_name === 'string' ? entry.customer_name : '',
        field: typeof entry.field === 'string' ? entry.field : '',
        message: typeof entry.message === 'string' ? entry.message : '',
      });
    }
  }
  return { imported, failed, errors };
}
